import type { FC, ReactNode } from 'react'
import type { StaffDuty } from '@/types'
import type { StaffBoardEntry } from '@/lib/carwash/selectors'

import { Check, CircleCheck, Coffee, Power } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Sheet } from '@/components/kit/sheet'
import { useToast } from '@/components/kit/toast'
import { useCarWashActions } from '@/hooks/use-carwash-actions'

interface AvailabilitySheetProps {
    entry: StaffBoardEntry | null
    open: boolean
    onClose: () => void
}

type Option = {
    duty: StaffDuty
    label: string
    detail: string
    icon: ReactNode
    iconClass: string
}

/* Available, on break or inactive. Busy is never set by hand: it follows the vehicles assigned. */
export const AvailabilitySheet: FC<AvailabilitySheetProps> = ({ entry, open, onClose }): ReactNode => {
    const actions = useCarWashActions()
    const toast = useToast()

    if (!entry) return null

    const busy = entry.jobs.length > 0
    const name = entry.staff.name

    const options: Option[] = [
        {
            duty: 'on_duty',
            label: busy ? 'On duty' : 'Available',
            detail: busy ? 'Busy now, available again after the current vehicle.' : 'Ready for the next vehicle.',
            icon: <CircleCheck />,
            iconClass: 'bg-emerald-50 text-emerald-700'
        },
        {
            duty: 'on_break',
            label: 'On break',
            detail: busy ? 'Starts the break after the current vehicle is done.' : 'Not offered for new vehicles until they are back.',
            icon: <Coffee />,
            iconClass: 'bg-amber-50 text-amber-700'
        },
        {
            duty: 'inactive',
            label: 'Inactive',
            detail: busy ? 'Finish or hand over the current vehicle first.' : 'Off duty or no longer working here. History is kept.',
            icon: <Power />,
            iconClass: 'bg-slate-100 text-slate-600'
        }
    ]

    const choose = (duty: StaffDuty) => {
        if (duty === entry.staff.duty) {
            onClose()
            return
        }

        if (!actions.setStaffDuty(entry.staff.id, duty)) return

        const message = duty === 'on_duty'
            ? `${name} is ${busy ? 'back on duty' : 'available'}`
            : duty === 'on_break' ? `${name} is on a break` : `${name} is now inactive`

        toast({ title: message, tone: duty === 'on_duty' ? 'success' : 'info' })
        onClose()
    }

    return (
        <Sheet open={open} onClose={onClose} title={`Availability for ${name}`} size='sm'>
            <div role='radiogroup' aria-label='Availability' className='space-y-2'>
                {options.map(option => {
                    const selected = entry.staff.duty === option.duty
                    const disabled = option.duty === 'inactive' && busy

                    return (
                        <button
                            key={option.duty}
                            type='button'
                            role='radio'
                            aria-checked={selected}
                            disabled={disabled}
                            onClick={() => choose(option.duty)}
                            className={cn(
                                'flex w-full items-center gap-3 rounded-xl border p-3 text-left transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-55',
                                selected ? 'border-primary bg-accent' : 'border-border bg-card hover:bg-slate-50'
                            )}
                        >
                            <span className={cn('grid size-10 shrink-0 place-items-center rounded-lg [&_svg]:size-5', option.iconClass)}>
                                {option.icon}
                            </span>

                            <span className='min-w-0 flex-1'>
                                <span className='block text-[15px] font-semibold text-foreground'>
                                    {option.label}
                                </span>

                                <span className='block text-[13px] text-muted-foreground'>
                                    {option.detail}
                                </span>
                            </span>

                            {selected && <Check className='size-5 shrink-0 text-primary' aria-hidden='true' />}
                        </button>
                    )
                })}
            </div>
        </Sheet>
    )
}
